import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
/* eslint-disable jsx-a11y/no-autofocus -- custom Modal prop focuses through react-focus-lock. */
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Button } from '@osmosis/tailwind-ui/components/Button';

import React, { MouseEvent, useState, useEffect } from 'react';
import { faCog } from '@fortawesome/pro-regular-svg-icons/faCog';
import { faCopy } from '@fortawesome/pro-regular-svg-icons/faCopy';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { faShareAlt } from '@fortawesome/pro-regular-svg-icons/faShareAlt';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { faList } from '@fortawesome/pro-regular-svg-icons/faList';
import { faListCheck } from '@fortawesome/pro-regular-svg-icons/faListCheck';

import useModal from '../../../hooks/useModal';
import { PlaylistProps, PlaylistType } from '@osmosis/shared/types/playlist';

import ActionMenu, { PopupMenuItem } from '../../../components/PopupMenu';
import ActionMenuRow from '../../../components/PopupMenuRow';
import CheckableActionRow from './PopupMenuRowWithCheckbox';
import InputEdit from '../../../components/InputEdit';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePlaylistContext } from '../../../context/PlaylistProvider';
import { useSnackbarContext } from '../../../context/SnackbarProvider';
import { useUserContext } from '../../../context/UserContext';

import {
  useFeatureFlagsContext,
  FeatureFlagsSet,
} from '../../../context/FeatureFlagsContext';
import { usePagesContext } from '../../../context/PagesProvider';
import Modal from '../../../components/Modal';
import DeletePlaylistModalBox from './DeletePlaylistModalBox';
import { faFacebookF } from '@fortawesome/free-brands-svg-icons/faFacebookF';
import { faLink } from '@fortawesome/pro-regular-svg-icons/faLink';
import StatusAnnouncement from '../../../components/Snackbar';
import CopyLinkSnackBar from '@osmosis/nextjs-components/Snackbar/CopyLinkSnackBar';
import { shareOnFacebook } from '../../../lib/frontend/share';
import ExpiredUserLockoutModal from '../../../components/learn/ExpiredUserModal';
import { ExpiredUserModalType } from '../../../constants/ExpiredUserModalConfig';
import {
  textGrayInvert,
  textNegativeText,
  bgBase,
  bgNegative,
  textBlue,
  text3,
  text5,
} from '@osmosis/tailwind-ui/styles';
import { useCreateDeepLink } from '@osmosis/nextjs-components/hooks/useCreateDeepLink';

type Props = {
  playlist: PlaylistProps;
  isFollowed: boolean;
  handleFollowing: () => void;
};

/**
 * PlaylistSettingsButton.
 */
const PlaylistSettingsButton = (props: Props) => {
  const { playlist, isFollowed, handleFollowing } = props;

  const { user, hasEolsToken } = useUserContext();
  const hasAdminAccess = Object.keys(user?.admin || {}).length > 0;
  const isPlaylistMine = playlist.userID === user?.userID && playlist.userOwned;
  const playlistLength = playlist.list.length;
  const currentPlaylistItems = playlist.list.map((item) => ({
    learnNodeID: item.data.nodeID,
    name: item.title,
  }));
  const {
    playlists,
    deletePlaylist,
    createPlaylist,
    copyPlaylistVideos,
    followPlaylist,
  } = usePlaylistContext();
  const { onAddSnackBar } = useSnackbarContext();
  const { push } = usePagesContext();
  const intl = useIntl();
  const { hasPrime } = useUserContext();
  const featureFlag = useFeatureFlagsContext();
  const { createDeepLink } = useCreateDeepLink();

  const getPlaylistsState = () =>
    playlists.map((p) => ({
      [p.url]: currentPlaylistItems.length
        ? currentPlaylistItems.every(
            (item) => !!p.list?.find((l) => l.data.nodeID === item.learnNodeID),
          )
        : false,
    }));

  const savedPlaylistState = useState(getPlaylistsState());
  const [isPlaylistCreateVisible, setPlaylistCreateVisible] = useState(false);
  const playlistCreateState = useState('');
  const [showCopySnackBar, setShowCopySnackBar] = useState(false);

  const togglePlaylistCreate = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (playlistCreateState[0]) playlistCreateState[1]('');
    setPlaylistCreateVisible((prev) => !prev);
  };

  const {
    isVisible: settingsMenuVisible,
    hide: hideSettingsMenu,
    toggle: toggleSettingsMenu,
  } = useModal();

  const {
    isVisible: duplicateMenuVisible,
    hide: hideDuplicateMenu,
    toggle: toggleDuplicateMenu,
  } = useModal();

  const {
    isVisible: deleteConfirmVisible,
    hide: hideDeleteConfirmMenu,
    toggle: toggledDeleteMenu,
  } = useModal();

  const {
    isVisible: shareMenuVisible,
    hide: hideShareMenu,
    toggle: toggleShareMenu,
  } = useModal();

  const nonPrimeLockoutModal = useModal();
  const nonPrimeModal = useModal();

  const closeAllMenus = () => {
    hideDuplicateMenu();
    setTimeout(hideSettingsMenu);
  };

  const handleToggleDelete = () => {
    closeAllMenus();
    toggledDeleteMenu();
  };

  const handleCloseActionsPopup = () => {
    if (duplicateMenuVisible) hideDuplicateMenu();
    setTimeout(hideSettingsMenu);
  };

  const handleDeletePlaylist = async () => {
    const res = await deletePlaylist(playlist.url);
    if (res?.success) {
      hideDeleteConfirmMenu();
      goToInitialPlaylist();
    }
  };

  const handlePlaylistCreate = async (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();

    if (hasPrime) {
      if (playlistCreateState[0]) {
        const { success, url, title } = await createPlaylist({
          title: playlistCreateState[0],
          options: {
            source: 'playlist-page',
          },
          onAddSnackBar,
        });
        if (success && url && title) {
          await copyPlaylistVideos({
            items: currentPlaylistItems,
            anotherPlaylistName: title,
            anotherPlaylistUrl: url,
            type: playlist.type,
            onAddSnackBar,
          });

          togglePlaylistCreate(e);
          closeAllMenus();
        }
      }
    } else {
      closeAllMenus();
      nonPrimeLockoutModal.show();
    }
  };

  const handleFollowPlaylist = async () => {
    if (hasPrime) {
      const res = await followPlaylist(playlist.url, isFollowed ? 0 : 1);
      if (res?.success) {
        handleFollowing();
      }
    } else {
      nonPrimeModal.show();
    }
  };

  const goToInitialPlaylist = () => {
    void push('/playlist');
  };

  const handleCopyLink = () => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(window.location.href).then(() => {
        setShowCopySnackBar(true);
        hideShareMenu();
      });
    }
  };

  const handleCreateDeepLink = async () => {
    await createDeepLink({
      url: window.location.href,
      playlistID: playlist.playlistID,
    });
  };

  const hideFacebookShare = featureFlag.isActive(
    FeatureFlagsSet.learnEmbedRemoval,
  );

  const settingsItems = [
    isPlaylistMine && {
      id: 'delete-playlist',
      text: intl.formatMessage({
        id: 'playlist.settings.button.delete_playlist',
        defaultMessage: 'Delete playlist',
      }),
      icon: faTimes,
      textColor: textNegativeText,
      iconColor: textNegativeText,
      onClick: handleToggleDelete,
    },
    {
      id: 'duplicate-playlist',
      text: intl.formatMessage({
        id: 'playlist.settings.button.duplicate_playlist',
        defaultMessage: 'Duplicate playlist',
      }),
      icon: faCopy,
      onClick: toggleDuplicateMenu,
    },
  ].filter(Boolean) as PopupMenuItem[];

  const shareItems = [
    {
      id: 'playlist_copy_link',
      text: intl.formatMessage({
        id: 'playlist.setttings.button.copy_link',
        defaultMessage: 'Copy Link',
      }),
      icon: faLink,
      onClick: handleCopyLink,
    },
    (user?.isFaculty || hasAdminAccess) &&
      hasEolsToken && {
        id: 'playlist_create_deep_link',
        text: intl.formatMessage({
          id: 'playlist.setttings.button.create_deep_link',
          defaultMessage: 'Create deep-link',
        }),
        icon: faLink,
        onClick: handleCreateDeepLink,
      },
    !hideFacebookShare && {
      id: 'playlist_share_facebook',
      text: intl.formatMessage({
        id: 'playlist.settings.button.facebook',
        defaultMessage: 'Facebook',
      }),
      icon: faFacebookF,
      onClick: shareOnFacebook,
    },
  ].filter(Boolean) as PopupMenuItem[];

  useEffect(() => {
    if (!duplicateMenuVisible && isPlaylistCreateVisible) {
      setPlaylistCreateVisible(false);
      playlistCreateState[1]('');
    }
  }, [duplicateMenuVisible]);

  useEffect(() => {
    if (settingsMenuVisible || duplicateMenuVisible) {
      closeAllMenus();
    }
  }, [playlist.url]);

  useEffect(() => {
    savedPlaylistState[1](getPlaylistsState());
  }, [playlists]);

  return (
    <>
      {!hasPrime && (
        <>
          <ExpiredUserLockoutModal
            {...nonPrimeLockoutModal}
            modalType={ExpiredUserModalType.PLAYLIST}
          />
          <ExpiredUserLockoutModal
            {...nonPrimeModal}
            modalType={ExpiredUserModalType.PLAYLIST}
          />
        </>
      )}
      <Modal
        hide={hideDeleteConfirmMenu}
        isVisible={deleteConfirmVisible}
        autoFocus
        headerContent={intl.formatMessage({
          id: 'playlist.settings.button.delete_playlist',
          defaultMessage: 'Delete playlist',
        })}
        hasHeader={true}
        headerBackground={bgNegative}
        hideIconClasses={`opacity-100 ${bgBase}`}
        headerClasses={`${textGrayInvert} m-auto font-bold`}
      >
        <DeletePlaylistModalBox
          handleClose={hideDeleteConfirmMenu}
          handleDelete={handleDeletePlaylist}
        />
      </Modal>

      {playlist.type !== PlaylistType.WATCH_LATER && (
        <Flex>
          {user && (
            <Box>
              <ActionMenu
                keyExtractor={(item) => item.id}
                isVisible={settingsMenuVisible}
                hide={handleCloseActionsPopup}
                disableHide={duplicateMenuVisible}
                hideOnScroll={false}
                hideEvent="wheel"
                renderItem={(item) => {
                  if (item.id === 'duplicate-playlist') {
                    return (
                      <ActionMenu
                        keyExtractor={(item) => item.id}
                        isVisible={duplicateMenuVisible}
                        hide={hideDuplicateMenu}
                        hideEvent="wheel"
                        hideOnScroll={!duplicateMenuVisible}
                        containerClasses="absolute top-full left-0 z-[1000] overflow-y-auto min-w-[220px]"
                        renderItem={(item) => (
                          <CheckableActionRow
                            item={item}
                            checkboxState={savedPlaylistState}
                            hasPrime={hasPrime}
                            nonPrimeLockoutModal={nonPrimeLockoutModal}
                            closeAllMenus={closeAllMenus}
                          />
                        )}
                        heading="Copy to..."
                        centerHeading
                        items={playlists
                          .filter((p) => p.url !== playlist.url)
                          .map((p) => ({
                            id: p.url,
                            text: p.title,
                            type: p.type,
                            items: playlist.list.map((item) => ({
                              learnNodeID: item.data.nodeID,
                              name: item.title,
                            })),
                            playlistName: p.title,
                            playlistUrl: p.url,
                          }))}
                        footer={
                          <>
                            {isPlaylistCreateVisible ? (
                              <Box className={`p-2`}>
                                <InputEdit
                                  inputState={playlistCreateState}
                                  onConfirm={handlePlaylistCreate}
                                  onCancel={togglePlaylistCreate}
                                  placeholder={intl.formatMessage({
                                    defaultMessage: 'Playlist name',
                                    id: 'create_playlist_bar.playlist_name',
                                  })}
                                  hideCancelBtn
                                />
                              </Box>
                            ) : (
                              <Flex className="w-full opacity-50">
                                <Button
                                  startIcon={<Icon icon={faPlus} />}
                                  variant="transparent"
                                  onClick={togglePlaylistCreate}
                                  data-testid="create-new-playlist-btn"
                                  className={`flex-1 ${text3} justify-center`}
                                >
                                  <FormattedMessage
                                    id="create-new-playlist-message"
                                    defaultMessage="Create new"
                                  />
                                </Button>
                              </Flex>
                            )}
                          </>
                        }
                      >
                        <ActionMenuRow item={item} />
                      </ActionMenu>
                    );
                  } else {
                    return <ActionMenuRow item={item} />;
                  }
                }}
                heading="Playlist actions"
                centerHeading
                items={
                  playlistLength ? settingsItems : settingsItems.slice(0, 1)
                }
              >
                <Box className="opacity-75">
                  <Button
                    startIcon={<Icon icon={faCog} />}
                    variant="outline"
                    onClick={toggleSettingsMenu}
                    data-testid="settings-playlist-btn"
                    className={`mr-2 ${text5}`}
                  >
                    <FormattedMessage
                      id="common.settings"
                      defaultMessage="Settings"
                    />
                  </Button>
                </Box>
              </ActionMenu>
            </Box>
          )}

          <ActionMenu
            keyExtractor={(item) => item.id}
            isVisible={shareMenuVisible}
            hide={hideShareMenu}
            renderItem={(item) => (
              <ActionMenuRow
                dataTestid={`popup-menu-item-${item.id}`}
                item={item}
              />
            )}
            heading={intl.formatMessage({
              id: 'playlist.settings.button.heading_share',
              defaultMessage: 'Share',
            })}
            centerHeading
            items={shareItems}
          >
            <Button
              startIcon={<Icon icon={faShareAlt} />}
              variant="outline"
              onClick={toggleShareMenu}
              data-testid="share-playlist-btn"
              color="blue"
              className="border-blue-100"
            >
              <FormattedMessage
                id="playlist-share-message"
                defaultMessage="Share"
              />
            </Button>
          </ActionMenu>

          {user && !isPlaylistMine && (
            <Button
              startIcon={<Icon icon={isFollowed ? faListCheck : faList} />}
              variant="outline"
              onClick={handleFollowPlaylist}
              data-testid="follow-playlist-btn"
              className={`ml-2 ${textBlue}`}
            >
              {isFollowed ? (
                <FormattedMessage
                  id="playlist-following-message"
                  defaultMessage="Following"
                />
              ) : (
                <FormattedMessage
                  id="playlist-follow-message"
                  defaultMessage="Follow"
                />
              )}
            </Button>
          )}
        </Flex>
      )}

      <StatusAnnouncement
        isVisible={showCopySnackBar}
        hide={() => setShowCopySnackBar(false)}
        message={<CopyLinkSnackBar />}
        variant="positive"
      />
    </>
  );
};

export default PlaylistSettingsButton;
